import React from 'react'
import { Download, FileText, Trash2 } from 'lucide-react'
import PqrSectionModal from './pqrPreview/PqrSectionModal'
import Button from '../ui/Button'
import type { ReportListItem } from '../../utils/reportsClient'
import { formatPqrDateTime } from '../../utils/formatPqrDateTime'

/**
 * Shared "all versions" modal surfaced from a TrainCard's
 * "Show all N versions" link on both the PQR and WPS history pages.
 *
 * Pure presentational — receives the latest-first real reports list
 * for one PQR# / WPS# train and forwards row-level Download / Remove
 * to the parent. Removing the latest still routes through the page's
 * `handleRemove` which gates with the existing `ConfirmDialog`.
 *
 * Built on the shared `PqrSectionModal` shell so backdrop / Esc /
 * portal semantics match the rest of the modals. The footer is
 * suppressed via `hideFooter` — the only close affordances are the
 * header X, Esc, and a backdrop click. Sized larger
 * (`max-w-[60vw]` / `h-[80vh]`) than the standard section modal so
 * long version lists breathe horizontally and vertically. The body
 * uses `h-[80vh]` (not `max-h-[80vh]`) so the modal is consistently
 * tall regardless of how many versions are present — the empty /
 * single-row state still occupies the full panel.
 */
export interface ReportVersionsModalProps {
  open: boolean
  title: string
  reports: ReportListItem[]
  busyId?: string | null
  onDownload: (report: ReportListItem) => void
  onRemove: (report: ReportListItem, meta: { isLatest: boolean }) => void
  onClose: () => void
}

function RevPill({ revision }: { revision: string | undefined }) {
  return (
    <span className="inline-flex items-center rounded-full bg-[rgba(0,39,63,0.06)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-blueprint-blue">
      v{typeof revision === 'string' && revision.length > 0 ? revision : '0'}
    </span>
  )
}

function LatestTag() {
  return (
    <span className="inline-flex items-center rounded-full bg-[rgba(31,154,58,0.08)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-galvanizing-green">
      Latest
    </span>
  )
}

export default function ReportVersionsModal({
  open,
  title,
  reports,
  busyId,
  onDownload,
  onRemove,
  onClose
}: ReportVersionsModalProps) {
  return (
    <PqrSectionModal
      open={open}
      title={title}
      isModalDirty={false}
      panelMaxWidthClassName="max-w-[60vw]"
      bodyMaxHeightClassName="h-[80vh]"
      hideFooter
      onApply={onClose}
      onCancel={onClose}
    >
      {reports.length === 0 ? (
        <p className="text-sm text-gray-500">No versions to show.</p>
      ) : (
        <ul
          className="flex flex-col gap-2"
          data-testid="report-versions-list"
        >
          {reports.map((r, idx) => {
            const isLatest = idx === 0
            const busy = busyId === r.id
            const email =
              r.generatedBy && r.generatedBy.email
                ? r.generatedBy.email
                : '\u2014'
            return (
              <li
                key={r.id}
                data-testid={`report-versions-row-${r.id}`}
                className="flex items-start justify-between gap-3 rounded-md border border-[#E2E5EA] bg-white px-3 py-2"
              >
                <div className="flex min-w-0 items-start gap-2.5">
                  <FileText className="mt-0.5 h-4 w-4 flex-shrink-0 text-blueprint-blue" />
                  <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <RevPill revision={r.revision} />
                      {isLatest && <LatestTag />}
                      <span className="min-w-0 truncate text-sm text-blueprint-blue">
                        {r.name}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-heritage-grey">
                      <span>{formatPqrDateTime(r.uploadedAt)}</span>
                      <span>by {email}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1.5">
                  <Button
                    variant="primary"
                    className="h-7 px-2 text-[11px]"
                    disabled={busy}
                    onClick={() => onDownload(r)}
                    data-testid={`report-versions-download-${r.id}`}
                    title="Download"
                    aria-label="Download"
                  >
                    <Download className="mr-1 h-3 w-3" />
                    Download
                  </Button>
                  <Button
                    variant="secondary"
                    className="h-7 px-2 text-[11px]"
                    disabled={busy}
                    onClick={() => onRemove(r, { isLatest })}
                    data-testid={`report-versions-remove-${r.id}`}
                    title="Remove"
                    aria-label="Remove"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </PqrSectionModal>
  )
}
